import { db } from '../firebase';
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';

// Subscription to tasks with robust initial load and fallback polling
// Sorts client-side by createdAt desc to include docs without the field
export function subscribeTasks(onUpdate, onError, onStatus) {
  const q = query(collection(db, 'tasks'));
  let poller = null;

  const log = (evt) => {
    try { if (typeof onStatus === 'function') onStatus({ ...evt, at: Date.now() }); } catch {}
  };

  const deliver = (docs, source) => {
    let list = docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => {
      const ac = a.createdAt ?? 0;
      const bc = b.createdAt ?? 0;
      return bc - ac; // newer first
    });
    log({ type: source || 'unknown', count: list.length });
    onUpdate(list);
  };

  // One-time prefetch to show data immediately
  (async () => {
    try {
      const snapOnce = await getDocs(q);
      deliver(snapOnce.docs, 'prefetch');
    } catch (e) {
      log({ type: 'error', error: e?.message, code: e?.code });
      if (onError) onError(e);
    }
  })();

  const startPolling = async () => {
    try {
      const snapOnce = await getDocs(q);
      deliver(snapOnce.docs, 'poll');
    } catch (e) {
      log({ type: 'error', error: e?.message, code: e?.code });
      if (onError) onError(e);
    }
    poller = setInterval(async () => {
      try {
        const snap = await getDocs(q);
        deliver(snap.docs, 'poll');
      } catch (e) {
        log({ type: 'error', error: e?.message, code: e?.code });
        if (onError) onError(e);
      }
    }, 10000);
  };

  const unsubRealtime = onSnapshot(
    q,
    (snap) => {
      if (poller) { clearInterval(poller); poller = null; }
      deliver(snap.docs, 'realtime');
    },
    (err) => {
      log({ type: 'error', error: err?.message, code: err?.code });
      if (onError) onError(err);
      // Fallback to polling when realtime channel fails
      startPolling();
    }
  );

  return () => {
    try { unsubRealtime(); } catch {}
    if (poller) clearInterval(poller);
    log({ type: 'unsub' });
  };
}

// Create a new task with required fields
export async function createTask({ name, description, categoryId }) {
  const payload = {
    name: (name ?? '').trim(),
    description: (description ?? '').trim(),
    categoryId,
    createdAt: Date.now(),
  };
  return addDoc(collection(db, 'tasks'), payload);
}

// Optional helpers for future edits/deletes
export async function updateTask(id, changes) {
  const payload = {};
  if (typeof changes.name === 'string') payload.name = changes.name.trim();
  if (typeof changes.description === 'string') payload.description = changes.description.trim();
  if (typeof changes.categoryId === 'string') payload.categoryId = changes.categoryId;
  return updateDoc(doc(db, 'tasks', id), payload);
}

export async function deleteTask(id) {
  return deleteDoc(doc(db, 'tasks', id));
}